(ns app.front.branding
  (:require [app.domain.branding.model :as branding-model]
            [app.front.entities :as entities]
            [core.front.color :as color]
            [re-frame.core-instrumented :as rf]
            [reitit.frontend.easy :as rfe]))

(defn install-new-css! [css]
  (let [element (js/document.createElement "style")]
    (set! (.-id element) "branding-style")
    (set! (.-type element) "text/css")
    (let [text-node (js/document.createTextNode css)]
      (.appendChild element text-node))
    (-> (js/document.getElementsByTagName "head")
        first
        (.appendChild element))))

(defn install-css! [css]
  (let [element (js/document.getElementById "branding-style")]
    (if element
      (let [text-node (js/document.createTextNode css)]
        (.appendChild element text-node))
      (install-new-css! css))))

(defn generate-css [{:group/keys [branding-background-hue]
                     bg          :group/branding-banner-bg-color
                     fg          :group/branding-banner-fg-color}]
  (let [banner-fg   (or fg "var(--ui-white)")
        banner-bg   (or bg "var(--ui-night-sky-100)")
        bg-hover    (or (some-> bg color/make-gray)
                        "var(--ui-night-sky-90)")
        fg-subtitle (or (some-> fg color/make-gray)
                        "#adb5bd") ;; gray-500
        main-bg     (or (some-> branding-background-hue (color/hsv->rgb 0.061 0.961))
                        "var(--ui-violet-10)")
        admin-bg    (or (some-> branding-background-hue (color/hsv->rgb 0.116 0.914))
                        "var(--ui-violet-20)")]
    (str ".header-text {color: " banner-fg ";}"
         ".cloudpermit--header--group-select .bx--text-input {color: " banner-fg " !important;}"
         ".header-text:hover {color: " banner-fg ";}"
         ".header-text:visited {color: " banner-fg " !important;}"
         ".header-hover:hover {background-color: " bg-hover ";}"
         ".header-divider {border-left: 2px solid " banner-fg ";}"
         ".header-dropdown-title {"
         "color: " fg-subtitle ";"
         "border-color: " fg-subtitle ";"
         "}"
         ".header-logo path {color: " banner-fg ";}"
         ".header-color {background-color: " banner-bg ";}"
         "body {background-color: " main-bg " !important;}"
         ".branded-background-color {background-color: " main-bg ";}"
         ".branded-admin-background-color {background-color: " admin-bg ";}")))

(rf/reg-fx :branding/generate-unbranded-css
  (fn [_]
    (->> {} generate-css install-css!)))

(rf/reg-event-fx :branding/generate-unbranded-css (constantly {:fx [[:branding/generate-unbranded-css]]}))

(rf/reg-fx :branding/generate-css
  (fn [config]
    (->> config generate-css install-css!)))

(defn images->map [images]
  (->> images
       (map (fn [[attr id]]
              [(branding-model/attribute->image-type attr) [:permanent id]]))
       (into {})))

(rf/reg-event-fx :branding/config-loaded
  (fn [{:keys [db]} [_ _ {:keys [config images group-type]}]]
    (let [images-map   (images->map images)
          superdomain? (= group-type :group.type/superdomain)]
      {:fx [(if superdomain?
              [:branding/generate-unbranded-css]
              [:branding/generate-css config])]
       :db (assoc db :branding/config config
                  :branding/config-initial config
                  :branding/images images-map
                  :branding/permanent-images images-map
                  :branding/loading? false
                  :branding/group-type group-type)})))

(rf/reg-event-fx :branding/set-branding
  (fn [{:keys [db]} [_ branding]]
    (if (or (nil? branding)
            (= branding branding-model/default-branding))
      {:fx [[:branding/generate-unbranded-css]]
       :db (-> (assoc db :branding/loading? false)
               (dissoc :branding :branding/group-type))}
      {:db (assoc db :branding          branding
                  :branding/loading? true)
       :fx [[:comm/command {:command    :group/get-branding-config
                            :method     :get
                            :data       {:brand branding}
                            :on-success [:branding/config-loaded]}]]})))

(rf/reg-event-fx :branding/update-config
  (fn [{:keys [db]} [_ key value]]
    (let [new-config (-> db :branding/config (assoc key value))]
      {:db (assoc db :branding/config new-config)
       :fx [[:branding/generate-css new-config]]})))

(rf/reg-event-db :branding/replace-image
  (fn [db [_ image-type file-id]]
    (assoc-in db [:branding/images image-type] [:temporary file-id])))


(rf/reg-event-db :branding/remove-image
  (fn [db [_ image-type]]
    (update db :branding/images dissoc image-type)))

(rf/reg-event-db :branding/clear-branding-images
  (fn [db _]
    (update db :branding/images #(->> %
                                      (filter (comp (partial = :permanent) first val))
                                      (into {})))))

(rf/reg-event-db :branding/reset-branding-images
  (fn [db [_ group-id]]
    (let [new-images (->> (get-in db [::entities/entity-db group-id])
                          branding-model/images-map
                          images->map)]
      (assoc db :branding/images new-images
             :branding/permanent-images new-images))))

(rf/reg-sub :branding/image-url
  (fn [db [_ image-type]]
    (let [branding          (:branding db)
          [type data]       (-> db :branding/images image-type)
          [_ fallback-data] (-> db :branding/permanent-images image-type)]
      (if data
        (cond
          (not branding)
          nil

          (= type :permanent)
          (branding-model/image-url branding (name image-type) data)

          (= type :temporary)
          (str "/api/command/user/download-unlinked-file?file-id=" data))
        (when fallback-data
          (branding-model/image-url branding (name image-type) fallback-data))))))

(rf/reg-sub :branding/image-exists?
  (fn [db [_ image-type]]
    (and (:branding db)
         (-> db :branding/images image-type boolean))))

;; Returns nil on default branding
#_{:clj-kondo/ignore [:re-frame.reg-sub/unnamespaced-key]}
(rf/reg-sub :branding
  :-> :branding)

;; Always returns a string
(rf/reg-sub :branding/as-string
  :<- [:branding]
  :-> branding-model/or-default)

;; Reload the page if the page isn't currently using the given branding
(rf/reg-event-fx :branding/force-branding
  (fn [{{:keys                                       [branding]
         {{:keys [name]} :data
          :keys          [path-params query-params]} :route/match} :db}
       [_ new-branding]]
    (when-not (= branding new-branding)
      (let [new-branding (or new-branding branding-model/default-branding)]
        #_{:clj-kondo/ignore [:discouraged-var]}
        {:fx [[:navigate-to (rfe/href name (assoc path-params :branding new-branding) query-params)]]}))))

;; We can't rely on the branding being set by the /:branding route, because the user might not have activated any route at all (non-existent URL)
;; Therefore we need to set some initial branding before the route sets the branding
(rf/reg-event-fx :branding/generate-initial-css
  (fn [{{:keys [initial-branding-set?] :as db} :db}]
    (when-not initial-branding-set?
      {:db       (assoc db :initial-branding-set? true)
       :dispatch [:branding/generate-unbranded-css]})))

(rf/reg-event-fx :branding/reset-branding
  (fn [{:keys [db]} _]
    (if (:branding db)
      (let [superdomain? (= :group.type/superdomain (:branding/group-type db))]
        {:db (assoc db :branding/config (:branding/config-initial db)
                    :branding/images (:branding/permanent-images db))
         :fx [(if superdomain?
                [:branding/generate-unbranded-css]
                [:branding/generate-css (:branding/config-initial db)])]})
      {:db (dissoc db :branding/config :branding/images :branding/group-type)
       :fx [[:branding/generate-unbranded-css]]})))

(rf/reg-sub :branding/config
  :-> :branding/config)

(rf/reg-sub :branding/group-type
  :-> :branding/group-type)
